import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Camera, Eye, Crown, Sparkles, Heart, MessageCircle } from 'lucide-react';
import { useRoom } from '../../../contexts/RoomContext';
import '../styles/UserMenu.css';

/**
 * ユーザーメニューページ
 * ゲストユーザーが写真投稿・ギャラリー閲覧を選択できるメニュー画面
 */
const UserMenu: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const { roomId, isRoomSet, setRoomId } = useRoom();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // roomId の確認とリダイレクト処理
  useEffect(() => {
    const checkRoomId = () => {
      // URLクエリパラメータからroomidを取得
      const roomidFromQuery = searchParams.get("roomid");
      
      if (roomidFromQuery) {
        // クエリパラメータにroomidがある場合、それを使用
        setRoomId(roomidFromQuery);
        sessionStorage.setItem("roomId", roomidFromQuery);
        setLoading(false);
      } else if (!isRoomSet || !roomId) {
        // クエリパラメータにもroomIdがない場合は sessionStorage から復元を試行
        const savedRoomId = sessionStorage.getItem("roomId");
        if (savedRoomId) {
          // sessionStorage からroomIdを復元
          setRoomId(savedRoomId);
          setLoading(false);
        } else {
          // sessionStorage にもない場合は guest-login にリダイレクト
          navigate('/user/guest-login');
          return;
        }
      } else {
        // roomId が既に設定されている場合
        setLoading(false);
      }
    };

    checkRoomId();
  }, [roomId, isRoomSet, setRoomId, navigate, searchParams]);

  // 写真投稿画面への遷移
  const handlePhotoUpload = () => {
    navigate('/user/photo-selection');
  };

  // 写真ギャラリー画面への遷移
  const handlePhotoGallery = () => {
    navigate('/user/photos');
  };

  // コメント入力画面への遷移
  const handleCommentInput = () => {
    navigate('/user/comment-input');
  };

  // ローディング表示
  if (loading) {
    return (
      <div className="user-menu-container">
        <div className="loading-section">
          <div className="elegant-loading-spinner"></div>
          <Crown className="loading-icon" />
          <p className="loading-text">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-menu-container">
      <div className="user-menu-background">
      </div>
      
      <div className="user-menu-card">
        {/* ヘッダー装飾 */}
        <div className="user-menu-header">
          <div className="header-decoration">
            <Crown className="crown-icon" />
            <Sparkles className="sparkle-icon sparkle-1" />
            <Sparkles className="sparkle-icon sparkle-2" />
          </div>
          <h1 className="user-menu-title">
            <Heart className="title-heart" />
            Photo Memorial
            <Heart className="title-heart" />
          </h1>
          <p className="user-menu-subtitle">Wedding Memory Menu</p>
        </div>

        {/* メニューセクション */}
        <div className="menu-section">
          <h2 className="menu-section-title">メニューを選択してください</h2>
          <p className="menu-section-description">
            思い出の写真を投稿するか、<br />これまでの写真を閲覧できます
          </p>

          {/* メニューボタン */}
          <div className="menu-buttons">
            <button 
              className="menu-button upload-button"
              onClick={handlePhotoUpload}
            >
              <div className="button-icon">
                <Camera size={24} />
              </div>
              <div className="button-content">
                <h3 className="button-title">写真を投稿</h3>
                <p className="button-description">
                  特別な瞬間を <br/>皆様と共有しましょう
                </p>
              </div>
              <div className="button-arrow">→</div>
            </button>

            <button 
              className="menu-button gallery-button"
              onClick={handlePhotoGallery}
            >
              <div className="button-icon">
                <Eye size={24} />
              </div>
              <div className="button-content">
                <h3 className="button-title">写真を見る</h3>
                <p className="button-description">
                  みんなの思い出を <br/>閲覧できます
                </p>
              </div>
              <div className="button-arrow">→</div>
            </button>

            <button 
              className="menu-button comment-button"
              onClick={handleCommentInput}
            >
              <div className="button-icon">
                <MessageCircle size={24} />
              </div>
              <div className="button-content">
                <h3 className="button-title">コメントを投稿</h3>
                <p className="button-description">
                  お祝いのメッセージを <br/>残しましょう
                </p>
              </div>
              <div className="button-arrow">→</div>
            </button>
          </div>
        </div>

        {/* フッター装飾 */}
        <div className="user-menu-footer">
          <div className="footer-decoration">
            <div className="decoration-line"></div>
            <Heart className="footer-heart" />
            <div className="decoration-line"></div>
          </div>
          <p className="footer-text">Create Beautiful Memories Together</p>
        </div>
      </div>
    </div>
  );
};

export default UserMenu;